import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueries } from '@tanstack/react-query';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import {
    organizationApi,
    paymentsInfrastructureApi,
    permissionsApi,
    secretsApi,
    userApi,
} from '@/lib/pillarApi';
import { useDataInvalidationStore } from '@/store/dataInvalidation';
import { PermissionGrantEditor } from '@/components/permissions/PermissionGrantEditor';
import {
    type BusinessDetails,
    resourceDesignatorKey,
    type PermissionsGrant,
    type PermissionsBundlePublic,
    type ResourceDesignator,
} from '@pillar-portal/api-types';
import {
    AuthorizableAction,
    ResourceType,
} from '@pillar-portal/database/dist/frontend';
import {
    formatPermissionActionVerbList,
    formatPermissionResourceTypePluralLabel,
    PermissionGrantSummary,
} from './PermissionGrantSummary';

function formatBusinessLabel(businessDetails?: BusinessDetails): string | null {
    const dba: string | undefined = businessDetails?.dba?.trim();
    if (dba !== undefined && dba.length > 0) {
        return dba;
    }
    const legalEntityName: string | undefined =
        businessDetails?.legalEntityName?.trim();
    if (legalEntityName !== undefined && legalEntityName.length > 0) {
        return legalEntityName;
    }
    return null;
}

function formatUserDisplayName(
    firstName: string | null,
    lastName: string | null,
    primaryEmail: string
): string {
    const fullName: string = `${firstName ?? ''} ${lastName ?? ''}`.trim();
    return fullName.length > 0 ? fullName : primaryEmail;
}

function usePolicyPermissionDesignatorLabelByKey(
    permissions: ReadonlyArray<PermissionsGrant>,
    organizationUuid: string,
    organizationName: string,
    bundle: PermissionsBundlePublic | null
): Readonly<Record<string, string>> {
    const distinctDesignators: ReadonlyArray<ResourceDesignator> = useMemo(() => {
        const seenKeys = new Set<string>();
        const nextDesignators: ResourceDesignator[] = [];
        for (const permission of permissions) {
            for (const resource of permission.resources) {
                if ('designator_type' in resource) {
                    continue;
                }
                const designatorKey = resourceDesignatorKey(resource);
                if (seenKeys.has(designatorKey)) {
                    continue;
                }
                seenKeys.add(designatorKey);
                nextDesignators.push(resource);
            }
        }
        return nextDesignators;
    }, [permissions]);

    const designatorLabelQueries = useQueries({
        queries: distinctDesignators.map((resource) => {
            const designatorKey = resourceDesignatorKey(resource);
            switch (resource.resource_type) {
                case ResourceType.ORGANIZATION:
                    return {
                        queryKey: ['permissionDesignatorLabel', designatorKey],
                        queryFn: async () => {
                            if (resource.organization_uuid === organizationUuid) {
                                return resource.descendants
                                    ? `Organization ${organizationName} and descendants`
                                    : `Organization ${organizationName}`;
                            }
                            const organization = await organizationApi.getOrganization(
                                resource.organization_uuid
                            );
                            const organizationLabel = organization.name;
                            return resource.descendants
                                ? `Organization ${organizationLabel} and descendants`
                                : `Organization ${organizationLabel}`;
                        },
                        staleTime: 30000,
                    } as const;
                case ResourceType.USER:
                    return {
                        queryKey: ['permissionDesignatorLabel', designatorKey],
                        queryFn: async () => {
                            const user = await userApi.getUser(resource.user_uuid);
                            return `User ${formatUserDisplayName(
                                user.first_name,
                                user.last_name,
                                user.primary_email
                            )}`;
                        },
                        staleTime: 30000,
                    } as const;
                case ResourceType.CREDENTIAL:
                    return {
                        queryKey: ['permissionDesignatorLabel', designatorKey],
                        queryFn: async () => {
                            const credential = await secretsApi.getSecret({
                                credential_uuid: resource.credential_uuid,
                            });
                            return `Credential ${credential.account_id}`;
                        },
                        staleTime: 30000,
                    } as const;
                case ResourceType.MERCHANT_ACCOUNT:
                    return {
                        queryKey: ['permissionDesignatorLabel', designatorKey],
                        queryFn: async () => {
                            const merchantAccount =
                                await paymentsInfrastructureApi.getMerchantAccount(
                                    resource.merchant_account_uuid
                                );
                            const businessLabel = formatBusinessLabel(
                                merchantAccount.businessDetails
                            );
                            return `Merchant account ${
                                businessLabel ??
                                `${merchantAccount.processor_name} ${merchantAccount.processor_mid}`
                            }`;
                        },
                        staleTime: 30000,
                    } as const;
                case ResourceType.LOGICAL_TERMINAL:
                    return {
                        queryKey: ['permissionDesignatorLabel', designatorKey],
                        queryFn: async () => {
                            const logicalTerminal =
                                await paymentsInfrastructureApi.getLogicalTerminal(
                                    resource.logical_terminal_uuid
                                );
                            const businessLabel = formatBusinessLabel(
                                logicalTerminal.businessDetails
                            );
                            return `Logical terminal ${
                                businessLabel ??
                                `${logicalTerminal.processor_name} ${logicalTerminal.processor_tid}`
                            }`;
                        },
                        staleTime: 30000,
                    } as const;
                case ResourceType.TERMINAL_DEVICE:
                    return {
                        queryKey: ['permissionDesignatorLabel', designatorKey],
                        queryFn: async () => {
                            const terminalDevice =
                                await paymentsInfrastructureApi.getTerminalDevice(
                                    resource.terminal_device_uuid
                                );
                            const terminalLabel =
                                terminalDevice.name?.trim() ??
                                terminalDevice.processor_device_id;
                            return `Terminal device ${terminalLabel}`;
                        },
                        staleTime: 30000,
                    } as const;
                case ResourceType.PERMISSIONS_BUNDLE:
                    return {
                        queryKey: ['permissionDesignatorLabel', designatorKey],
                        queryFn: async () => {
                            if (
                                bundle !== null &&
                                bundle.permissions_bundle_uuid ===
                                    resource.permissions_bundle_uuid
                            ) {
                                return `Permissions bundle ${bundle.name}`;
                            }
                            return `Permissions bundle ${resource.permissions_bundle_uuid}`;
                        },
                        staleTime: Infinity,
                    } as const;
                case ResourceType.PERMISSION_POLICY:
                    return {
                        queryKey: ['permissionDesignatorLabel', designatorKey],
                        queryFn: async () => {
                            if (
                                bundle !== null &&
                                bundle.permissions_bundle_uuid ===
                                    resource.permission_policy_uuid
                            ) {
                                return `Permission policy ${bundle.name}`;
                            }
                            return `Permission policy ${resource.permission_policy_uuid}`;
                        },
                        staleTime: Infinity,
                    } as const;
                default:
                    return {
                        queryKey: ['permissionDesignatorLabel', designatorKey],
                        queryFn: async () => undefined,
                        staleTime: Infinity,
                    } as const;
            }
        }),
    });

    return useMemo(() => {
        const nextLabelByKey: Record<string, string> = {};
        for (let index = 0; index < distinctDesignators.length; index += 1) {
            const designator = distinctDesignators[index];
            if (designator === undefined) {
                continue;
            }
            const queryResult = designatorLabelQueries[index];
            const resolvedLabel = queryResult?.data;
            if (resolvedLabel === undefined) {
                continue;
            }
            nextLabelByKey[resourceDesignatorKey(designator)] = resolvedLabel;
        }
        return nextLabelByKey;
    }, [designatorLabelQueries, distinctDesignators]);
}

type PolicyPermissionBundleModalMode = 'create' | 'edit' | 'view';

interface PolicyPermissionBundleModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    mode: PolicyPermissionBundleModalMode;
    organizationId: number | null;
    organizationUuid: string;
    organizationName: string;
    bundle: PermissionsBundlePublic | null;
}

function buildEmptyPolicyPermission(): PermissionsGrant {
    return {
        resource_type: ResourceType.CREDENTIAL,
        actions: [],
        resources: [],
    };
}

interface PolicyPermissionEditorModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    organizationUuid: string;
    organizationName: string;
    policyName: string;
    permission: PermissionsGrant | null;
    onSavePermission: (permission: PermissionsGrant) => void;
}

function PolicyPermissionEditorModal(props: PolicyPermissionEditorModalProps) {
    const [draftPermission, setDraftPermission] = useState<PermissionsGrant>(
        buildEmptyPolicyPermission()
    );
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        if (!props.open) {
            setDraftPermission(buildEmptyPolicyPermission());
            setValidationError(null);
            return;
        }
        setDraftPermission(props.permission ?? buildEmptyPolicyPermission());
        setValidationError(null);
    }, [props.open, props.permission]);

    const modalDescription: string = useMemo(() => {
        if (draftPermission.actions.length === 0) {
            return 'Select actions to grant permissions for.';
        }
        const policyName =
            props.policyName.trim().length > 0 ? props.policyName.trim() : 'unnamed policy';
        return `Grant ${formatPermissionActionVerbList(draftPermission.actions)} permissions to policy ${policyName} of ${props.organizationName} on ${formatPermissionResourceTypePluralLabel(draftPermission.resource_type)}`;
    }, [
        draftPermission.actions,
        draftPermission.resource_type,
        props.organizationName,
        props.policyName,
    ]);

    const handleSave = (): void => {
        if (draftPermission.actions.length === 0) {
            setValidationError('Select at least one action');
            return;
        }
        if (draftPermission.resources.length === 0) {
            setValidationError('Select at least one source designator');
            return;
        }
        setValidationError(null);
        props.onSavePermission(draftPermission);
        props.onOpenChange(false);
    };

    return (
        <Dialog open={props.open} onOpenChange={props.onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        {props.permission === null ? 'Add Permission' : 'Edit Permission'}
                    </DialogTitle>
                    <DialogDescription>{modalDescription}</DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    <PermissionGrantEditor
                        value={draftPermission}
                        onChange={(nextPermission) => {
                            setDraftPermission(nextPermission);
                            setValidationError(null);
                        }}
                        eligibleActions={Object.values(AuthorizableAction)}
                        organizationUuid={props.organizationUuid}
                    />

                    {validationError !== null && (
                        <Alert variant="destructive">
                            <AlertDescription>{validationError}</AlertDescription>
                        </Alert>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => props.onOpenChange(false)}
                    >
                        Cancel
                    </Button>
                    <Button type="button" onClick={handleSave}>
                        Save Permission
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function PolicyPermissionBundleModal(props: PolicyPermissionBundleModalProps) {
    const bumpPolicyBundlesVersion = useDataInvalidationStore(
        (state) => state.bumpPolicyBundlesVersion
    );
    const [nameText, setNameText] = useState<string>('');
    const [descriptionText, setDescriptionText] = useState<string>('');
    const [permissions, setPermissions] = useState<ReadonlyArray<PermissionsGrant>>([]);

    const [isPermissionEditorOpen, setIsPermissionEditorOpen] = useState<boolean>(false);
    const [editingPermissionIndex, setEditingPermissionIndex] = useState<number | null>(null);
    const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState<boolean>(false);
    const [pendingRemovePermissionIndex, setPendingRemovePermissionIndex] = useState<number | null>(
        null
    );

    const [isConfirmRemovePermissionOpen, setIsConfirmRemovePermissionOpen] =
        useState<boolean>(false);
    const bundleDeserializationError: string | null =
        props.bundle?.permissions_deserialization_error ?? null;
    const hasInvalidPersistedPermissions: boolean = bundleDeserializationError !== null;
    const isReadOnly: boolean = props.mode === 'view';

    useEffect(() => {
        if (!props.open) {
            return;
        }
        if (props.mode === 'create') {
            setNameText('');
            setDescriptionText('');
            setPermissions([]);
            setEditingPermissionIndex(null);
            return;
        }
        if (props.bundle === null) {
            return;
        }
        setNameText(props.bundle.name);
        setDescriptionText(props.bundle.description ?? '');
        setPermissions(props.bundle.permissions);
        setEditingPermissionIndex(null);
    }, [props.open, props.mode, props.bundle]);

    const normalizedDescription: string | undefined = useMemo(() => {
        const trimmed: string = descriptionText.trim();
        return trimmed.length > 0 ? trimmed : undefined;
    }, [descriptionText]);

    const editingPermission: PermissionsGrant | null = useMemo(() => {
        if (editingPermissionIndex === null) {
            return null;
        }
        return permissions[editingPermissionIndex] ?? null;
    }, [editingPermissionIndex, permissions]);
    const designatorLabelByKey = usePolicyPermissionDesignatorLabelByKey(
        permissions,
        props.organizationUuid,
        props.organizationName,
        props.bundle
    );

    const createMutation = useMutation({
        mutationFn: async () => {
            const createName: string = nameText.trim();
            if (createName.length === 0) {
                throw new Error('Name is required');
            }
            return await permissionsApi.createPolicyPermissionBundleForOrganization(
                props.organizationUuid,
                {
                    name: createName,
                    description: normalizedDescription,
                    permissions: [...permissions],
                }
            );
        },
        onSuccess: () => {
            bumpPolicyBundlesVersion();
            props.onOpenChange(false);
        },
        onError: (err) => {
            console.error('Error creating policy bundle:', err);
        },
    });

    const updateMutation = useMutation({
        mutationFn: async () => {
            if (props.bundle === null) {
                throw new Error('Missing bundle to update');
            }
            const updateName: string = nameText.trim();
            if (updateName.length === 0) {
                throw new Error('Name is required');
            }
            return await permissionsApi.updatePolicyPermissionBundle(
                props.bundle.permissions_bundle_uuid,
                {
                    name: updateName,
                    description: normalizedDescription,
                    permissions: [...permissions],
                }
            );
        },
        onSuccess: () => {
            bumpPolicyBundlesVersion();
            props.onOpenChange(false);
        },
        onError: (err) => {
            console.error('Error updating policy bundle:', err);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async () => {
            if (props.bundle === null) {
                throw new Error('Missing bundle to delete');
            }
            return await permissionsApi.deletePolicyPermissionBundle(
                props.bundle.permissions_bundle_uuid
            );
        },
        onSuccess: () => {
            bumpPolicyBundlesVersion();
            props.onOpenChange(false);
        },
        onError: (err) => {
            console.error('Error deleting policy bundle:', err);
        },
    });

    const errorMessage: string | null = useMemo(() => {
        const createError: unknown = createMutation.error;
        if (createError instanceof Error) {
            return createError.message;
        }
        const updateError: unknown = updateMutation.error;
        if (updateError instanceof Error) {
            return updateError.message;
        }
        const deleteError: unknown = deleteMutation.error;
        if (deleteError instanceof Error) {
            return deleteError.message;
        }
        return null;
    }, [createMutation.error, deleteMutation.error, updateMutation.error]);

    const isSaving: boolean = createMutation.isPending || updateMutation.isPending;
    const isDeleting: boolean = deleteMutation.isPending;

    return (
        <>
            <Dialog open={props.open} onOpenChange={props.onOpenChange}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {props.mode === 'create'
                                ? 'Create Policy Bundle'
                                : props.mode === 'edit'
                                  ? 'Edit Policy Bundle'
                                  : 'View Policy Bundle'}
                        </DialogTitle>
                        <DialogDescription>
                            {props.mode === 'create'
                                ? 'Create a shared policy bundle with typed permissions.'
                                : props.mode === 'edit'
                                  ? 'Edit this shared policy bundle and its typed permissions.'
                                  : 'View this shared policy bundle and its typed permissions.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6">
                        {errorMessage !== null && (
                            <Alert variant="destructive">
                                <AlertDescription>{errorMessage}</AlertDescription>
                            </Alert>
                        )}

                        {hasInvalidPersistedPermissions && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {bundleDeserializationError}
                                    {
                                        ' Saving this bundle will overwrite the invalid persisted permissions.'
                                    }
                                </AlertDescription>
                            </Alert>
                        )}

                        <div className="space-y-2">
                            <Label>Name</Label>
                            <Input
                                value={nameText}
                                onChange={(e) => setNameText(e.target.value)}
                                placeholder="Cashier"
                                disabled={isReadOnly}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Input
                                value={descriptionText}
                                onChange={(e) => setDescriptionText(e.target.value)}
                                placeholder="Optional"
                                disabled={isReadOnly}
                            />
                        </div>

                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <Label>Permissions</Label>
                                {!isReadOnly && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            setEditingPermissionIndex(null);
                                            setIsPermissionEditorOpen(true);
                                        }}
                                    >
                                        + Permission
                                    </Button>
                                )}
                            </div>

                            {permissions.length === 0 ? (
                                <p className="text-sm text-muted-foreground">
                                    No permissions in this policy bundle.
                                </p>
                            ) : (
                                <div className="space-y-4">
                                    {permissions.map((permission, index) => (
                                        <div key={index} className="border rounded-lg p-4">
                                            <div className="flex justify-between items-start gap-4">
                                                <div className="flex-1">
                                                    <PermissionGrantSummary
                                                        permission={permission}
                                                        designatorLabelByKey={
                                                            designatorLabelByKey
                                                        }
                                                    />
                                                </div>

                                                {!isReadOnly && (
                                                    <div className="flex gap-2">
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => {
                                                                setEditingPermissionIndex(index);
                                                                setIsPermissionEditorOpen(true);
                                                            }}
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                            onClick={() => {
                                                                setPendingRemovePermissionIndex(
                                                                    index
                                                                );
                                                                setIsConfirmRemovePermissionOpen(
                                                                    true
                                                                );
                                                            }}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <DialogFooter className="gap-2">
                        {props.mode === 'edit' && (
                            <Button
                                type="button"
                                variant="destructive"
                                onClick={() => setIsConfirmDeleteOpen(true)}
                                disabled={isSaving || isDeleting}
                            >
                                Delete
                            </Button>
                        )}
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => props.onOpenChange(false)}
                            disabled={isSaving || isDeleting}
                        >
                            {isReadOnly ? 'Close' : 'Cancel'}
                        </Button>
                        {!isReadOnly && (
                            <Button
                                type="button"
                                onClick={() => {
                                    if (props.mode === 'create') {
                                        createMutation.mutate();
                                        return;
                                    }
                                    updateMutation.mutate();
                                }}
                                disabled={isSaving || isDeleting}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <PolicyPermissionEditorModal
                open={isPermissionEditorOpen}
                onOpenChange={(nextOpen) => {
                    setIsPermissionEditorOpen(nextOpen);
                    if (!nextOpen) {
                        setEditingPermissionIndex(null);
                    }
                }}
                organizationUuid={props.organizationUuid}
                organizationName={props.organizationName}
                policyName={nameText}
                permission={editingPermission}
                onSavePermission={(permission) => {
                    if (editingPermissionIndex === null) {
                        setPermissions([...permissions, permission]);
                        return;
                    }
                    setPermissions(
                        permissions.map((currentPermission, index) => {
                            return index === editingPermissionIndex
                                ? permission
                                : currentPermission;
                        })
                    );
                    setEditingPermissionIndex(null);
                }}
            />

            <ConfirmationModal
                open={isConfirmDeleteOpen}
                onOpenChange={setIsConfirmDeleteOpen}
                message="Delete this policy bundle?"
                confirmLabel="Delete"
                onConfirm={() => {
                    setIsConfirmDeleteOpen(false);
                    deleteMutation.mutate();
                }}
                onCancel={() => setIsConfirmDeleteOpen(false)}
                isLoading={isDeleting}
            />

            <ConfirmationModal
                open={isConfirmRemovePermissionOpen}
                onOpenChange={setIsConfirmRemovePermissionOpen}
                message="Remove this permission from the policy bundle?"
                confirmLabel="Remove"
                onConfirm={() => {
                    if (pendingRemovePermissionIndex === null) {
                        return;
                    }
                    setPermissions(
                        permissions.filter((_permission, index) => {
                            return index !== pendingRemovePermissionIndex;
                        })
                    );
                    setPendingRemovePermissionIndex(null);
                    setIsConfirmRemovePermissionOpen(false);
                }}
                onCancel={() => {
                    setPendingRemovePermissionIndex(null);
                    setIsConfirmRemovePermissionOpen(false);
                }}
            />
        </>
    );
}
